import { useEffect, useReducer, useRef } from 'react'
import { useQuery, useApolloClient } from '@apollo/client'
import { AdvancedFilterFieldsQuery, normalizeValuesRequired, BOOLEAN_OPTIONS, fieldVisual, sameOptionValue } from './AdvancedFilters'
import { useAdvancedFilters } from './filterUrl'
import { RELATION_OPTIONS_REGISTRY, decodeGlobalId } from './relationOptions'
import './AdvancedFilters.css'

function hexToRgba(hex, alpha) {
    const clean = hex.replace('#', '')
    const r = parseInt(clean.substring(0, 2), 16)
    const g = parseInt(clean.substring(2, 4), 16)
    const b = parseInt(clean.substring(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// Shows the resolved label once relationLabelCache has it (see the
// resolution effect below); until then, falls back to the id so the chip
// never looks blank while the lookup is in flight.
function relationValueText(relationLabelCache, filterValues, valuesRequired) {
    if (valuesRequired === 'list' && Array.isArray(filterValues)) {
        if (filterValues.length === 0) return ''
        if (filterValues.length === 1) return relationLabelCache.current[filterValues[0]] || `#${decodeGlobalId(filterValues[0])}`
        return `${filterValues.length} selected`
    }
    if (!filterValues) return ''
    return relationLabelCache.current[filterValues] || `#${decodeGlobalId(filterValues)}`
}

function formatFilterValue(field, filterType, filterValues) {
    const operator = field?.allowedFilterTypes?.find((o) => o.value === filterType)
    const valuesRequired = normalizeValuesRequired(operator?.valuesRequired)
    if (valuesRequired === 'none') return ''

    const optionLabel = (v) => field?.options?.find((o) => sameOptionValue(v, o.value))?.label ?? v

    if (valuesRequired === 'two' && Array.isArray(filterValues)) {
        return `${filterValues[0] ?? ''} – ${filterValues[1] ?? ''}`
    }
    if (valuesRequired === 'list' && Array.isArray(filterValues)) {
        return filterValues.map((v) => (field?.fieldKind === 'enum' ? optionLabel(v) : v)).join(', ')
    }
    if (field?.fieldKind === 'boolean') {
        return BOOLEAN_OPTIONS.find((o) => sameOptionValue(filterValues, o.value))?.label ?? (filterValues ?? '')
    }
    if (field?.fieldKind === 'enum') {
        return optionLabel(filterValues)
    }
    if (field?.fieldKind === 'datetime' && typeof filterValues === 'string') {
        return filterValues.replace('T', ' ')
    }
    return filterValues ?? ''
}

// Applied advanced filters render alongside the existing filter chips, colored
// per fieldKind so a chip's color matches that field's color in the Advanced
// Filters dropdown. Reads the current filters straight from the `?filter=`
// URL param (via useAdvancedFilters) rather than taking them as a prop, and
// writes back to the same URL param on removal -- so the caller just needs to
// say which entity/context this list is filtering, nothing else.
// `hiddenFields` suppresses chips for fields the current view cannot act on --
// a kanban's grouping field, whose filter the page also strips from the query.
// It hides the chip only; the filter stays in the URL and comes back with its
// chip as soon as a view can honour it again.
function AppliedAdvancedFilters({ entity, context, presets = [], onPresetsChange = () => {}, hiddenFields = [] }) {
    const [filters, setFilters] = useAdvancedFilters()
    const isHidden = (fieldName) => (hiddenFields || []).includes(fieldName)
    const visiblePresets = presets.filter((preset) => preset.visible !== false && !isHidden(preset.fieldName))
    const hasAnything = !!(filters && filters.length) || visiblePresets.length > 0
    const apolloClient = useApolloClient()
    // id -> label for relation values, resolved on demand below (this
    // component only ever sees raw ids -- no label is ever passed in).
    const relationLabelCache = useRef({})
    const [, bumpRelationLabels] = useReducer((c) => c + 1, 0)
    const { data } = useQuery(AdvancedFilterFieldsQuery, {
        variables: { entity, context },
        skip: !hasAnything,
        fetchPolicy: 'cache-first',
    })

    const fieldsByName = (data?.filterFields || []).reduce((acc, f) => { acc[f.fieldName] = f; return acc }, {})

    useEffect(() => {
        if (!hasAnything) return
        filters.forEach((filter) => {
            const field = fieldsByName[filter.fieldName]
            if (field?.fieldKind !== 'relation') return
            const registryEntry = RELATION_OPTIONS_REGISTRY[field.relationModel]
            if (!registryEntry) return
            const ids = Array.isArray(filter.filterValues) ? filter.filterValues : (filter.filterValues ? [filter.filterValues] : [])
            ids.forEach((id) => {
                if (!id || relationLabelCache.current[id] !== undefined) return
                relationLabelCache.current[id] = null
                registryEntry.resolveById(apolloClient, id).then((option) => {
                    if (option) relationLabelCache.current[id] = option.label
                    bumpRelationLabels()
                })
            })
        })
        visiblePresets.forEach((preset) => {
            const field = fieldsByName[preset.fieldName]
            if (field?.fieldKind !== 'relation') return
            const registryEntry = RELATION_OPTIONS_REGISTRY[field.relationModel]
            if (!registryEntry) return
            const ids = Array.isArray(preset.filterValues) ? preset.filterValues : (preset.filterValues ? [preset.filterValues] : [])
            ids.forEach((id) => {
                if (!id || relationLabelCache.current[id] !== undefined) return
                relationLabelCache.current[id] = null
                registryEntry.resolveById(apolloClient, id).then((option) => {
                    if (option) relationLabelCache.current[id] = option.label
                    bumpRelationLabels()
                })
            })
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, presets, data])

    if (!hasAnything) return null

    const removeAt = (index) => setFilters(filters.filter((_, i) => i !== index))

    const renderChip = (field, fieldName, filterType, filterValues, key, trailing) => {
        const operator = field?.allowedFilterTypes?.find((o) => o.value === filterType)
        const valuesRequired = normalizeValuesRequired(operator?.valuesRequired)
        const value = field?.fieldKind === 'relation'
            ? relationValueText(relationLabelCache, filterValues, valuesRequired)
            : formatFilterValue(field, filterType, filterValues)
        const visual = fieldVisual(field?.fieldKind)
        return (
            <span
                className="advanced-filter-chip"
                style={{ backgroundColor: hexToRgba(visual.color, 0.08) }}
                key={key}
            >
                <span className="advanced-filter-chip__field" style={{ color: visual.color }}>{field?.label || fieldName}</span>
                {operator && <span className="advanced-filter-chip__operator" style={{ color: hexToRgba(visual.color, 0.75) }}>{operator.label}</span>}
                {value !== '' && <span className="advanced-filter-chip__value" style={{ color: visual.color }}>"{value}"</span>}
                {trailing(visual)}
            </span>
        )
    }

    const presetChips = visiblePresets.map((preset) => {
        const field = fieldsByName[preset.fieldName]
        // A constrained preset (the page supplied `choices`) is editable only
        // from the popover and cannot be removed, so it gets no remove icon --
        // and no lock icon either, which would wrongly imply its value is frozen.
        const constrained = Array.isArray(preset.choices) && preset.choices.length > 0
        return renderChip(field, preset.fieldName, preset.filterType, preset.filterValues, preset.id, (visual) => (
            constrained ? null : preset.editable ? (
                <i className="fa fa-times-circle advanced-filter-chip__remove" style={{ color: visual.color }} aria-label="Remove filter"
                   onClick={() => onPresetsChange(presets.filter((p) => p.id !== preset.id))}></i>
            ) : (
                <i className="fa fa-lock advanced-filter-chip__lock" style={{ color: visual.color }} title="Set by this page" aria-hidden="true"></i>
            )
        ))
    })

    // Mapped, not pre-filtered: removeAt() addresses the URL's filter array by
    // index, so hiding a chip must not shift the ones after it.
    const filterChips = filters.map((filter, index) => {
        if (isHidden(filter.fieldName)) return null
        const field = fieldsByName[filter.fieldName]
        return renderChip(field, filter.fieldName, filter.filterType, filter.filterValues, `${filter.fieldName}-${filter.filterType}-${index}`, (visual) => (
            <i className="fa fa-times-circle advanced-filter-chip__remove" style={{ color: visual.color }} aria-label="Remove filter" onClick={() => removeAt(index)}></i>
        ))
    })

    return [...presetChips, ...filterChips]
}

export default AppliedAdvancedFilters
